import { C, SectionLabel, H2, wrap, useIsMobile } from '../styles';
import Photo from './Photo';
import { PHOTOS, GALLERY_SLUGS } from '../content/photos';

// Task 4 — social proof, placed immediately after the hero stat bar and before the
// PROCESS section so trust lands before anyone is asked to read four numbered steps.
//
// TWO HONESTY CONSTRAINTS RUN THROUGH THIS WHOLE FILE. Read them before editing copy:
//
//  1. These photographs are our partner's shop's own completed work for his own
//     customers. They are NOT jobs that came through MetalMatch. Every line below is
//     written to stay true of that. Do not "improve" any of it into implying these
//     were MetalMatch projects.
//  2. No counters, no "projects completed", no "trusted by", no shop count. The
//     instruction was explicit: frame it as vetted Ontario fabricators, never "50+
//     shops". And no named testimonials — the one real customer who would give a
//     quote is being held until MetalMatch has actually delivered for them.
//
// The partner is deliberately not named and his company is deliberately not named or
// linked. See the brief, Task 4 — the Instagram has been dormant since Dec 2019 and a
// name that verifies badly is weaker than no name at all.
const tiles = [
  {
    t: '37 years in Ontario fabrication between us',
    b: "Our partner Ian has 31 years in the trade and runs a Toronto steel supply business — he vets every fabricator who joins the network. Michael brings 6 years on the sourcing side.",
  },
  {
    t: 'We supply the steel ourselves',
    b: "Material comes through our partner's own steel business, not a third-party quote we're waiting on. That's why turnaround is measured in days.",
  },
  {
    t: 'Brief prepared in 48–72 hours',
    b: 'We turn your description and sketch into a structured brief and get it in front of matched shops.',
  },
  {
    t: 'Free to submit, no obligation',
    b: "No fee to send us a project. Compare the quotes and walk away if they don't work. Nothing reaches a shop floor without your approval.",
  },
];

export default function SocialProof() {
  const isMobile = useIsMobile();
  return (
    <section id="real-work" style={{ padding: isMobile ? '64px 0' : '88px 0', borderTop: `1px solid ${C.bdr}` }}>
      <div style={wrap}>
        <SectionLabel>Real work, real shops</SectionLabel>
        <H2 style={{ maxWidth: 760, marginBottom: 18 }}>This is what comes out the other end.</H2>
        <p style={{ color: C.mut, fontSize: 16, lineHeight: 1.75, maxWidth: 600, marginBottom: isMobile ? 32 : 44 }}>
          MetalMatch is built by people who work in Ontario fabrication. Every photo on this page is
          real work from our partner's shop floor — photographed on the job, not rendered. Nothing
          here is stock and nothing here is AI.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 14, marginBottom: isMobile ? 36 : 52,
        }}>
          {tiles.map(({ t, b }) => (
            <div key={t} style={{ background: C.surf, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: isMobile ? 20 : 24 }}>
              <h3 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 21, fontWeight: 700, textTransform: 'uppercase', color: C.txt, lineHeight: 1.15, marginBottom: 8 }}>{t}</h3>
              <p style={{ color: C.mut, fontSize: 14, lineHeight: 1.7 }}>{b}</p>
            </div>
          ))}
        </div>

        {/* Gallery. Every tile is lazy-loaded — this section sits below the fold on
            every viewport, and the hero is the only image on the page allowed to be
            eager. Captions are the manifest alt text verbatim; because the caption
            and the alt are the same string, the caption is hidden from assistive tech
            so a screen reader does not read the description twice per photo. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? 10 : 14,
        }}>
          {GALLERY_SLUGS.map((slug) => (
            <figure key={slug} style={{ margin: 0, background: C.surf, border: `1px solid ${C.bdr}`, borderRadius: 10, overflow: 'hidden' }}>
              <Photo
                slug={slug}
                ratio="4 / 3"
                sizes={'(max-width: 768px) 46vw, (max-width: 1100px) 31vw, 341px'}
              />
              <figcaption aria-hidden="true" style={{ padding: isMobile ? '10px 12px' : '12px 14px', fontSize: isMobile ? 11 : 12, lineHeight: 1.5, color: C.mut }}>
                {PHOTOS[slug].alt}
              </figcaption>
            </figure>
          ))}
        </div>

        {/* The line that does the most work on this page. It converts the weakness —
            no traction, no reviews — into the one thing a tradesperson responds to.
            It is also, unlike a testimonial we do not have, completely true. Keep it. */}
        <p style={{
          marginTop: isMobile ? 32 : 44,
          padding: isMobile ? '22px 20px' : '30px 34px',
          background: C.surf2, border: `1px solid ${C.bdr}`, borderLeft: `3px solid ${C.orange}`,
          borderRadius: 10,
          color: C.txt, fontSize: isMobile ? 15 : 17, lineHeight: 1.7, maxWidth: 820,
        }}>
          MetalMatch is new. We could have filled this page with testimonials nobody wrote and
          renderings of parts nobody built. Instead, here is the actual work our network produces.{' '}
          <strong style={{ color: C.txt }}>Judge us on the steel.</strong>
        </p>
      </div>
    </section>
  );
}
